/**
 * VirtVSwitchForm — Full-screen detail view for Virtual Switches
 * Tabs: General | Port Groups
 * Supports Standard (per-hypervisor), Distributed (per-cluster),
 * OVS, Linux Bridge, and Hyper-V virtual switches.
 */

import React, { useEffect, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  ArrowsRightLeftIcon,
  ServerStackIcon,
  CubeIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

// ── Constants ─────────────────────────────────────────────────────────────────

const VSWITCH_TYPES = [
  { value: 'standard',        label: 'Standard vSwitch',        icon: '🔀', scope: 'hypervisor', hint: 'VMware ESXi / local' },
  { value: 'distributed',     label: 'Distributed Switch (DVS)', icon: '☁️', scope: 'cluster',    hint: 'VMware vCenter/NSX' },
  { value: 'ovs',             label: 'Open vSwitch (OVS)',       icon: '🔧', scope: 'hypervisor', hint: 'KVM / OpenStack' },
  { value: 'hyper_v_external',label: 'Hyper-V External Switch',  icon: '🪟', scope: 'hypervisor', hint: 'Microsoft Hyper-V' },
  { value: 'linux_bridge',    label: 'Linux Bridge',             icon: '🐧', scope: 'hypervisor', hint: 'KVM / Proxmox' },
];

const PG_TYPES = [
  { value: 'vm',         label: 'VM Network',  color: '#2563eb' },
  { value: 'vmkernel',   label: 'VMkernel',    color: '#7c3aed' },
  { value: 'management', label: 'Management',  color: '#dc2626' },
  { value: 'storage',    label: 'Storage',     color: '#d97706' },
];

const EMPTY_FORM = {
  name: '',
  vswitch_type: 'standard',
  hypervisor_id: '',
  cluster_id: '',
  uplink_count: 2,
  mtu: 1500,
  notes: '',
};

const EMPTY_PG = { name: '', vlan_id: '', pg_type: 'vm', notes: '' };

// ── Helper ────────────────────────────────────────────────────────────────────

let _pgLocalId = 0;
const newLocalId = () => `local_${++_pgLocalId}`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function VirtVSwitchForm({ vswitch, companyId, onClose, onSave, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = Boolean(vswitch?.id);
  /* La ficha se abre EN LECTURA (clic en la fila del listado); 'create' y 'edit'
     editan. Sin `initialMode` edita, como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);

  // ── Supporting data ──
  const [hypervisors, setHypervisors] = useState([]);
  const [clusters, setClusters]       = useState([]);
  const [ipamVlans, setIpamVlans]     = useState([]);  // VLANs from IPAM

  // ── Form ──
  const [form, setForm]     = useState(EMPTY_FORM);
  const [isDirty, setIsDirty] = useState(false);

  // ── Port groups ──
  const [portGroups, setPortGroups]       = useState([]);  // {id?, _lid, name, vlan_id, pg_type, notes, _deleted}
  const [pgDraft, setPgDraft]             = useState(null); // null = closed, {} = open new pg form
  const [editingPg, setEditingPg]         = useState(null); // {pg, draft}

  // ── UI ──
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving]       = useState(false);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este vSwitch',
    actionLabel: 'pasas a otro vSwitch o cierras la ficha',
    save: () => handleSubmit({ keepEditing: editing }),
  });
  const [savedOk, setSavedOk]    = useState(false);
  const [apiError, setApiError]   = useState('');
  const [errors, setErrors]       = useState([]);

  // ── Load supporting data ──
  useEffect(() => {
    if (!companyId) return;
    api.getHypervisors(companyId).then(r => setHypervisors(r.data || [])).catch(() => {});
    api.getClusters(companyId).then(r => setClusters(r.data || [])).catch(() => {});
    api.getIpamVlans(companyId).then(r => setIpamVlans(r.vlans || r.data || [])).catch(() => {});
  }, [companyId]);

  // ── Populate form ──
  useEffect(() => {
    if (!vswitch) { setForm(EMPTY_FORM); setPortGroups([]); return; }
    setForm({
      name:          vswitch.name          || '',
      vswitch_type:  vswitch.vswitch_type  || 'standard',
      hypervisor_id: vswitch.hypervisor_id ?? '',
      cluster_id:    vswitch.cluster_id    ?? '',
      uplink_count:  vswitch.uplink_count  ?? 2,
      mtu:           vswitch.mtu           ?? 1500,
      notes:         vswitch.notes         || '',
    });
    setPortGroups(
      (vswitch.port_groups || []).map(pg => ({ ...pg, _lid: newLocalId() }))
    );
  }, [vswitch]);

  // ── Derived ──
  const typeInfo      = VSWITCH_TYPES.find(t => t.value === form.vswitch_type) || VSWITCH_TYPES[0];
  const scopeIsCluster = typeInfo.scope === 'cluster';
  const visiblePgs    = portGroups.filter(pg => !pg._deleted);
  const selectedHyp   = hypervisors.find(h => String(h.id) === String(form.hypervisor_id));
  const selectedClus  = clusters.find(c => String(c.id) === String(form.cluster_id));
  const scopeLabel    = scopeIsCluster
    ? (selectedClus?.name || '— Sin cluster —')
    : (selectedHyp?.name  || '— Sin hypervisor —');

  // ── Handlers ──
  const sf = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setIsDirty(true);
    setApiError('');
    setErrors([]);
  };

  const handleTypeChange = (t) => {
    const newType = VSWITCH_TYPES.find(x => x.value === t);
    setForm(p => ({
      ...p,
      vswitch_type: t,
      // Clear cluster if switching to hypervisor-scoped type and vice versa
      ...(newType?.scope === 'cluster'    ? { hypervisor_id: '' } : {}),
      ...(newType?.scope === 'hypervisor' ? { cluster_id: '' }    : {}),
    }));
    setIsDirty(true);
  };

  const handleDiscard = () => {
    if (vswitch) {
      setForm({
        name: vswitch.name || '', vswitch_type: vswitch.vswitch_type || 'standard',
        hypervisor_id: vswitch.hypervisor_id ?? '', cluster_id: vswitch.cluster_id ?? '',
        uplink_count: vswitch.uplink_count ?? 2, mtu: vswitch.mtu ?? 1500, notes: vswitch.notes || '',
      });
      setPortGroups((vswitch.port_groups || []).map(pg => ({ ...pg, _lid: newLocalId() })));
    } else {
      onClose?.();
    }
    setIsDirty(false);
    setErrors([]);
    setApiError('');
  };

  // Port Group helpers ──────────────────────────────────────────────────────────

  const addPortGroup = () => {
    setPgDraft({ ...EMPTY_PG });
  };

  const confirmAddPortGroup = () => {
    if (!pgDraft?.name?.trim()) return;
    setPortGroups(prev => [...prev, { ...pgDraft, _lid: newLocalId() }]);
    setPgDraft(null);
    setIsDirty(true);
  };

  const removePortGroup = (lid) => {
    setPortGroups(prev => prev.map(pg =>
      pg._lid === lid
        ? { ...pg, _deleted: true }
        : pg
    ));
    setIsDirty(true);
  };

  const startEditPg = (pg) => {
    setEditingPg({ lid: pg._lid, draft: { name: pg.name, vlan_id: pg.vlan_id ?? '', pg_type: pg.pg_type, notes: pg.notes || '' } });
  };

  const confirmEditPg = () => {
    if (!editingPg?.draft?.name?.trim()) return;
    setPortGroups(prev => prev.map(pg =>
      pg._lid === editingPg.lid ? { ...pg, ...editingPg.draft } : pg
    ));
    setEditingPg(null);
    setIsDirty(true);
  };

  // Main Save ──────────────────────────────────────────────────────────────────

  const handleSubmit = async ({ keepEditing = false } = {}) => {
    const errs = [];
    if (!form.name.trim()) errs.push('El nombre es obligatorio');
    if (!form.vswitch_type)  errs.push('El tipo es obligatorio');
    if (errs.length) { setErrors(errs); return false; }

    setSaving(true);
    setApiError('');
    try {
      const payload = {
        ...form,
        uplink_count: Number(form.uplink_count) || 2,
        mtu:          Number(form.mtu)          || 1500,
        hypervisor_id: form.hypervisor_id ? Number(form.hypervisor_id) : null,
        cluster_id:    form.cluster_id    ? Number(form.cluster_id)    : null,
      };

      let creado = null;
      if (!isEditing) {
        // For new: include all non-deleted port_groups in payload
        payload.port_groups = visiblePgs.map(pg => ({
          name:    pg.name,
          vlan_id: pg.vlan_id ? Number(pg.vlan_id) : null,
          pg_type: pg.pg_type,
        }));
        creado = registroGuardado(await api.createVSwitch(payload, companyId));
      } else {
        // Update main fields
        await api.updateVSwitch(vswitch.id, payload, companyId);

        // Sync port groups: delete removed, create new, update edited
        const toDelete = portGroups.filter(pg => pg._deleted && pg.id);
        const toCreate = portGroups.filter(pg => !pg._deleted && !pg.id);
        const toUpdate = portGroups.filter(pg => !pg._deleted && pg.id);

        await Promise.all(toDelete.map(pg => api.deletePortGroup(vswitch.id, pg.id, companyId).catch(() => {})));
        await Promise.all(toCreate.map(pg => api.createPortGroup(vswitch.id, {
          name:    pg.name,
          vlan_id: pg.vlan_id ? Number(pg.vlan_id) : null,
          pg_type: pg.pg_type,
          notes:   pg.notes || null,
        }, companyId).catch(() => {})));

        // Update only those that changed from original
        const origMap = Object.fromEntries((vswitch.port_groups || []).map(pg => [pg.id, pg]));
        await Promise.all(toUpdate
          .filter(pg => {
            const o = origMap[pg.id];
            return o && (pg.name !== o.name || pg.vlan_id !== o.vlan_id || pg.pg_type !== o.pg_type || pg.notes !== o.notes);
          })
          .map(pg => api.updatePortGroup(vswitch.id, pg.id, {
            name:    pg.name,
            vlan_id: pg.vlan_id ? Number(pg.vlan_id) : null,
            pg_type: pg.pg_type,
            notes:   pg.notes || null,
          }, companyId).catch(() => {}))
        );
      }

      setIsDirty(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      if (isCards && !keepEditing) setEditing(false);
      return true;
    } catch (err) {
      setApiError(err.message || 'Error guardando vSwitch');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: 'general',    label: isCards ? 'Resumen' : 'General' },
    { key: 'portgroups', label: `Port Groups${visiblePgs.length ? ` (${visiblePgs.length})` : ''}` },
  ];
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          vSwitches
        </button>
        <DetailCrumb label={form.name} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="vSwitch"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => { setIsDirty(false); onNavigate?.(destino); })}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />

        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {(apiError || errors.length > 0) && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError || errors[0]}
          </span>
        )}

        <div className="dv-topbar-actions">
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
            </button>
          ) : !editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditing(true)}>
                <CheckIcon style={{ width: 14, height: 14 }} /> Editar
              </button>
            </>
          ) : (
            <>
              {isDirty && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
                  <XMarkIcon /> Descartar
                </button>
              )}
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={handleSubmit}
                disabled={saving || !isDirty}
              >
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        {/* Type */}
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('general')} title="Tipo de vSwitch">
          <span className="dv-smart-btn-icon">
            <ArrowsRightLeftIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.8rem' }}>
            {typeInfo.icon} {typeInfo.label.split(' ')[0]}
          </span>
          <span className="dv-smart-btn-label">Tipo</span>
        </button>

        {/* Scope */}
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('general')} title={scopeIsCluster ? 'Cluster' : 'Hypervisor'}>
          <span className="dv-smart-btn-icon">
            {scopeIsCluster
              ? <CubeIcon style={{ width: 18, height: 18, color: '#0369a1' }} />
              : <ServerStackIcon style={{ width: 18, height: 18, color: '#0369a1' }} />
            }
          </span>
          <span className="dv-smart-btn-value" style={{
            color: (selectedHyp || selectedClus) ? '#0369a1' : '#94a3b8',
            fontSize: '0.75rem', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {scopeLabel}
          </span>
          <span className="dv-smart-btn-label">{scopeIsCluster ? 'Cluster' : 'Hypervisor'}</span>
        </button>

        {/* Uplinks */}
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('general')} title="Número de uplinks">
          <span className="dv-smart-btn-value" style={{ color: '#059669', fontWeight: 700 }}>
            {form.uplink_count || 2}
          </span>
          <span className="dv-smart-btn-label">Uplinks</span>
        </button>

        {/* MTU */}
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('general')} title="MTU configurado">
          <span className="dv-smart-btn-value" style={{ color: '#6b7280', fontSize: '0.8rem', fontFamily: 'monospace' }}>
            {form.mtu || 1500}
          </span>
          <span className="dv-smart-btn-label">MTU</span>
        </button>

        {/* Port Groups */}
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('portgroups')} title="Port groups">
          <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontWeight: 700 }}>
            {visiblePgs.length}
          </span>
          <span className="dv-smart-btn-label">Port Groups</span>
        </button>
        {vswitch?.id && (
          <DocSmartButton elementType="virt_vswitch" elementId={vswitch.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={e => { e.preventDefault(); handleSubmit(); }}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<SwatchIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => sf('name', v)}
                placeholder="Nombre del vSwitch…"
                editing={editing}
                saving={saving}
                onSave={handleSubmit}
                onCancel={handleDiscard}
                onEdit={() => setEditing(true)}
                crumb={[typeInfo.label, scopeLabel, `MTU ${form.mtu || 1500}`].filter(Boolean).join(' › ')}
                badges={[
                  { text: typeInfo.label },
                  { text: scopeIsCluster ? 'Cluster' : 'Hypervisor' },
                ]}
                chips={[
                  { value: visiblePgs.length, label: 'port groups', onClick: () => setActiveTab('portgroups') },
                  { value: form.uplink_count || 2, label: 'uplinks' },
                  { value: form.mtu || 1500, label: 'MTU' },
                ]}
                extra={vswitch?.id ? (
                  <DocSmartButton elementType="virt_vswitch" elementId={vswitch.id} elementName={form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>
              <input
                className="dv-record-title"
                value={form.name}
                onChange={e => sf('name', e.target.value)}
                placeholder="Nombre del vSwitch…"
                required
              />
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabs.map(tab => (
                  <TabButton
                    key={tab.key}
                    type="button"
                    className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
          <fieldset className="dv-fieldset" disabled={!editing}>

          {/* ── Tab: General ── */}
          {activeTab === 'general' && (
            <div className={paneClass}>
              {isCards && (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>Tipo</span><b style={{ fontSize: '0.95rem' }}>{typeInfo.label}</b></div>
                  <div className="sf2-kpi"><span>Ámbito</span><b style={{ fontSize: '0.95rem' }}>{scopeLabel}</b></div>
                  <div className="sf2-kpi"><span>Uplinks</span><b>{form.uplink_count || 2}</b></div>
                  <div className="sf2-kpi"><span>MTU</span><b>{form.mtu || 1500}</b></div>
                  <div className="sf2-kpi"><span>Port groups</span><b>{visiblePgs.length}</b></div>
                </div>
              )}
              <div className="dv-section">
                <div className="dv-section-title">Tipo y tecnología</div>
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="form-label">Tipo de vSwitch <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={form.vswitch_type}
                      onChange={e => handleTypeChange(e.target.value)}
                    >
                      {VSWITCH_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.icon} {t.label} — {t.hint}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Uplinks</label>
                    <input
                      type="number" min="0" max="64"
                      className="form-control"
                      value={form.uplink_count}
                      onChange={e => sf('uplink_count', e.target.value)}
                      placeholder="2"
                    />
                    <small className="text-muted">NICs físicas uplink</small>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">MTU</label>
                    <input
                      type="number" min="1500" max="9000"
                      className="form-control"
                      value={form.mtu}
                      onChange={e => sf('mtu', e.target.value)}
                      placeholder="1500"
                    />
                    <small className="text-muted">1500 estándar / 9000 jumbo</small>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">
                  {scopeIsCluster ? 'Cluster (DVS / Distribuido)' : 'Hypervisor (vSwitch local)'}
                </div>
                <div className="row g-3">
                  {!scopeIsCluster ? (
                    <div className="col-md-6">
                      <label className="form-label">Hypervisor</label>
                      <select
                        className="form-select"
                        value={form.hypervisor_id}
                        onChange={e => sf('hypervisor_id', e.target.value)}
                      >
                        <option value="">— Sin asignar —</option>
                        {hypervisors.map(h => (
                          <option key={h.id} value={h.id}>
                            {h.hypervisor_type_label ? `${h.hypervisor_type_label} · ` : ''}{h.name}
                            {h.mgmt_ip ? ` · ${h.mgmt_ip}` : ''}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">Asigna este vSwitch a un host ESXi / KVM / Hyper-V concreto</small>
                    </div>
                  ) : (
                    <div className="col-md-6">
                      <label className="form-label">Cluster vCenter / Distributed</label>
                      <select
                        className="form-select"
                        value={form.cluster_id}
                        onChange={e => sf('cluster_id', e.target.value)}
                      >
                        <option value="">— Sin asignar —</option>
                        {clusters.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.cluster_type_label ? `${c.cluster_type_label} · ` : ''}{c.name}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">Los DVS abarcan todos los hosts del cluster</small>
                    </div>
                  )}

                  {/* Context info card */}
                  {(selectedHyp || selectedClus) && (
                    <div className="col-md-6">
                      <div style={{ background: 'rgba(59,130,246,.08)', border: '1px solid rgba(59,130,246,.25)', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginTop: 28 }}>
                        {selectedHyp && (
                          <>
                            <div style={{ fontWeight: 600, color: '#93c5fd' }}>{selectedHyp.name}</div>
                            <div style={{ color: 'rgba(255,255,255,.5)' }}>{selectedHyp.hypervisor_type_label || selectedHyp.hypervisor_type}</div>
                            {selectedHyp.mgmt_ip && <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'rgba(255,255,255,.35)' }}>{selectedHyp.mgmt_ip}</div>}
                            <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 12 }}>{selectedHyp.vm_count || 0} VMs · {selectedHyp.version || 'versión desconocida'}</div>
                          </>
                        )}
                        {selectedClus && (
                          <>
                            <div style={{ fontWeight: 600, color: '#93c5fd' }}>{selectedClus.name}</div>
                            <div style={{ color: 'rgba(255,255,255,.5)' }}>{selectedClus.cluster_type_label || selectedClus.cluster_type}</div>
                            <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 12 }}>
                              {selectedClus.node_count || 0} nodos · {selectedClus.vm_count || 0} VMs
                              {selectedClus.ha_enabled ? ' · HA' : ''}
                              {selectedClus.drs_enabled ? ' · DRS' : ''}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Port Groups ── */}
          {activeTab === 'portgroups' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Port Groups / VLANs</span>
                  <button
                    type="button"
                    className="dv-btn dv-btn-secondary"
                    style={{ fontSize: 12, padding: '4px 12px' }}
                    onClick={addPortGroup}
                  >
                    <PlusIcon style={{ width: 14 }} /> Añadir Port Group
                  </button>
                </div>

                {/* Add Port Group inline form */}
                {pgDraft !== null && (
                  <div style={{ background: 'rgba(16,185,129,.15)', border: '1px solid rgba(16,185,129,.4)', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: '2 1 160px' }}>
                        <label style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>Nombre *</label>
                        <input
                          className="form-control form-control-sm"
                          value={pgDraft.name}
                          onChange={e => setPgDraft(d => ({ ...d, name: e.target.value }))}
                          placeholder="ej. VM-Network-100"
                          autoFocus
                        />
                      </div>
                      <div style={{ flex: '2 1 180px' }}>
                        <label style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>VLAN (IPAM)</label>
                        <select
                          className="form-select form-select-sm"
                          value={pgDraft.vlan_id}
                          onChange={e => setPgDraft(d => ({ ...d, vlan_id: e.target.value }))}
                        >
                          <option value="">— Sin tag (untagged) —</option>
                          {ipamVlans.map(v => (
                            <option key={v.id} value={v.vlan_id}>
                              VLAN {v.vlan_id}{v.name ? ` — ${v.name}` : ''}{v.site ? ` [${v.site}]` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: '1 1 120px' }}>
                        <label style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>Tipo</label>
                        <select
                          className="form-select form-select-sm"
                          value={pgDraft.pg_type}
                          onChange={e => setPgDraft(d => ({ ...d, pg_type: e.target.value }))}
                        >
                          {PG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" className="dv-btn dv-btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={confirmAddPortGroup}>Añadir</button>
                        <button type="button" className="dv-btn dv-btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => setPgDraft(null)}>Cancelar</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Port groups table */}
                {visiblePgs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>🔌</div>
                    <div style={{ fontWeight: 500 }}>Sin port groups</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Añade port groups para segmentar el tráfico de red por VLANs.</div>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        {['Nombre', 'VLAN ID', 'Tipo', 'VMs'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: '#6b7280', fontWeight: 500, fontSize: 12 }}>{h}</th>
                        ))}
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visiblePgs.map(pg => {
                        const pgType = PG_TYPES.find(t => t.value === pg.pg_type) || PG_TYPES[0];
                        const isEditRow = editingPg?.lid === pg._lid;
                        return (
                          <tr key={pg._lid} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            {isEditRow ? (
                              <>
                                <td style={{ padding: '6px 10px' }}>
                                  <input
                                    className="form-control form-control-sm"
                                    value={editingPg.draft.name}
                                    onChange={e => setEditingPg(e2 => ({ ...e2, draft: { ...e2.draft, name: e.target.value } }))}
                                    autoFocus
                                  />
                                </td>
                                <td style={{ padding: '6px 10px' }}>
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ minWidth: 160 }}
                                    value={editingPg.draft.vlan_id ?? ''}
                                    onChange={e => setEditingPg(e2 => ({ ...e2, draft: { ...e2.draft, vlan_id: e.target.value } }))}
                                  >
                                    <option value="">— Sin tag —</option>
                                    {ipamVlans.map(v => (
                                      <option key={v.id} value={v.vlan_id}>
                                        VLAN {v.vlan_id}{v.name ? ` — ${v.name}` : ''}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td style={{ padding: '6px 10px' }}>
                                  <select
                                    className="form-select form-select-sm"
                                    value={editingPg.draft.pg_type}
                                    onChange={e => setEditingPg(e2 => ({ ...e2, draft: { ...e2.draft, pg_type: e.target.value } }))}
                                  >
                                    {PG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                  </select>
                                </td>
                                <td style={{ padding: '6px 10px', color: '#9ca3af' }}>—</td>
                                <td style={{ padding: '6px 10px' }}>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <button type="button" className="dv-btn dv-btn-primary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={confirmEditPg}>✓</button>
                                    <button type="button" className="dv-btn dv-btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setEditingPg(null)}>✕</button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td style={{ padding: '7px 10px', fontWeight: 500 }}>{pg.name}</td>
                                <td style={{ padding: '7px 10px', color: pg.vlan_id ? '#1d4ed8' : '#9ca3af' }}>
                                  {(() => {
                                    if (!pg.vlan_id) return <span style={{ fontStyle: 'italic', fontSize: 12 }}>Sin tag</span>;
                                    const v = ipamVlans.find(x => x.vlan_id === Number(pg.vlan_id));
                                    return (
                                      <span title={v ? `VLAN ${v.vlan_id} — ${v.name}${v.site ? ` [${v.site}]` : ''}` : undefined}>
                                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>VLAN {pg.vlan_id}</span>
                                        {v?.name && <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 4 }}>{v.name}</span>}
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td style={{ padding: '7px 10px' }}>
                                  <span style={{
                                    background: `${pgType.color}22`, color: pgType.color,
                                    borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 600,
                                  }}>{pgType.label}</span>
                                </td>
                                <td style={{ padding: '7px 10px', color: '#9ca3af', fontSize: 12 }}>
                                  {pg.vm_nic_count != null ? `${pg.vm_nic_count} NICs` : isEditing ? '—' : '—'}
                                </td>
                                <td style={{ padding: '7px 10px' }}>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <button
                                      type="button"
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 2 }}
                                      onClick={() => startEditPg(pg)}
                                      title="Editar"
                                    >✏️</button>
                                    <button
                                      type="button"
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 2 }}
                                      onClick={() => removePortGroup(pg._lid)}
                                      title="Eliminar"
                                    >🗑</button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <small className="text-muted" style={{ marginTop: 8, display: 'block' }}>
                  Los cambios en port groups se guardan junto con el formulario al pulsar "Guardar".
                </small>
              </div>
            </div>
          )}

          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={() => handleSubmit()}
          />
          </FormCardsShell>
        </form>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="virt_vswitch"
          ciId={vswitch?.id}
          tipoLegible="el switch virtual"
        />
      </div>
    </div>
  );
}
